import React, { type ReactElement, useEffect, useRef, useState } from "react";
import { IoMdClose } from "react-icons/io";

import { clearUserData, isBetaMode, setBetaMode } from "@utilities/helper";
import { motion, useDragControls } from "motion/react";

// Draggable "Test Mode" badge shown while beta mode is active. Visiting any
// page with `?set-beta=1` prompts for the beta password and, when it matches
// `GATSBY_BETA_PASSWORD`, unlocks beta mode on production. Closing the badge
// leaves beta mode, clears the session and reloads without the query string.
const BetaMode = (): ReactElement => {
  // `isBetaMode()` reads a cookie and the current hostname, neither of which
  // exists during SSR, so the badge is only evaluated after hydration.
  const [isHydrated, setIsHydrated] = useState<boolean>(false);
  useEffect(() => {
    setIsHydrated(true);

    const betaParam = new URLSearchParams(window.location.search).get(
      "set-beta",
    );
    if (betaParam && parseInt(betaParam) === 1) {
      // Fail closed: with no password configured the unlock is unavailable
      // rather than openable with an empty prompt.
      const expectedPassword = process.env.GATSBY_BETA_PASSWORD ?? "";
      const betaPassword = prompt("Password:");
      if (expectedPassword !== "" && betaPassword === expectedPassword) {
        setBetaMode(true);
      } else {
        closeBetaMode();
      }
    }
  }, []);

  const constraintsRef = useRef(null);
  const dragControls = useDragControls();

  const closeBetaMode = (): void => {
    setBetaMode(false);
    clearUserData();
    if (window.location) {
      window.location.replace(
        window.location.origin + window.location.pathname,
      );
    }
  };

  return (
    <>
      {isHydrated && isBetaMode() && (
        <div
          className="pointer-events-none fixed left-1/2 top-1/2 z-30 flex h-[calc(100dvh-4rem)] w-[calc(100%-6rem)] -translate-x-1/2 -translate-y-1/2 flex-row items-end justify-start"
          ref={constraintsRef}
          onPointerDown={event => {
            dragControls.start(event);
          }}
        >
          <motion.div
            className="pointer-events-auto inline-flex cursor-grab items-center justify-center gap-2 rounded-full border border-primary-300 bg-primary/70 px-4 py-2 shadow-md backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            drag
            dragConstraints={constraintsRef}
            dragControls={dragControls}
          >
            <span className="text-sm font-semibold uppercase text-white">
              Test Mode
            </span>
            <button
              type="button"
              className="group cursor-pointer rounded-full bg-white p-0.5 text-primary transition hover:bg-white/80 focus:outline-none"
              onClick={closeBetaMode}
            >
              <IoMdClose className="size-4 transition duration-300 group-hover:rotate-90" />
            </button>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default BetaMode;
